import { useEffect } from "react";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import { supabase } from "@/lib/supabase";
import { ORG_SYNC_ROUTER_OBSERVE_ENABLED, SUPABASE_SYNC_DEBUG } from "@/lib/dataRuntimeFlags";
import { recordRealtimeMetric } from "@/lib/queryObservability";

export type SyncEvent = {
  type:
    | "job.updated"
    | "lead.updated"
    | "quote.updated"
    | "issue.updated"
    | "contractor.updated"
    | "warehouse.shipment.updated"
    | "warehouse.receiving.updated"
    | "warehouse.allocation.updated"
    | "message.created"
    | "execution.proof.created"
    | "checklist.item.updated";
  organizationId: string;
  entityId: string;
  parentId?: string;
  changedFields?: string[];
  version?: string;
  emittedAt: string;
};

function isSyncEvent(value: unknown): value is SyncEvent {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<SyncEvent>;
  return Boolean(candidate.type && candidate.organizationId && candidate.entityId && candidate.emittedAt);
}

export function useOrganizationSyncRouter(): void {
  useEffect(() => {
    if (!ORG_SYNC_ROUTER_OBSERVE_ENABLED) return;

    let disposed = false;
    let channel: ReturnType<typeof supabase.channel> | null = null;

    const connect = async () => {
      const organizationId = await getCurrentOrganizationId().catch(() => null);
      if (disposed || !organizationId) return;

      channel = supabase
        .channel(`org:${organizationId}:sync`)
        .on("broadcast", { event: "sync" }, ({ payload }) => {
          if (!isSyncEvent(payload)) return;
          recordRealtimeMetric({
            source: "org-sync-router",
            event: "broadcast",
            table: payload.type,
            entityId: payload.entityId,
            detail: SUPABASE_SYNC_DEBUG ? { payload } : undefined,
          });
        })
        .subscribe((status) => {
          if (SUPABASE_SYNC_DEBUG) {
            console.info("[org-sync-router] status", { organizationId, status });
          }
        });
    };

    void connect();

    return () => {
      disposed = true;
      if (channel) void supabase.removeChannel(channel);
    };
  }, []);
}
